import React, { useEffect } from 'react';
import queryString from 'query-string';
import { useEnvironment, useBi, useExperiments } from '@wix/yoshi-flow-editor';
import settingsParams from '../../../settingsParams';
import stylesParams from '../../../stylesParams';
import { useCascadingSettings } from '../../../Settings/useCascadingSettings';
import { useSettings } from '@wix/tpa-settings/react';
import s from './ProductPageBody.scss';
import Headline from '../Headline/Headline';
import ProductImage from '../ProductImage/ProductImage';
import ProductDescription from '../ProductDescription/ProductDescription';
import ProductPrice from '../ProductPrice/ProductPrice';
import ProductVariants from '../ProductVariants/ProductVariants';
import EnterCustomAmount from '../EnterCustomAmount/EnterCustomAmount';
import ProductBuyForSelection from '../ProductBuyForSelection/ProductBuyForSelection';
import ProductC2cForm from '../ProductC2cForm/ProductC2cForm';
import ProductDeliveryChannels from '../ProductDeliveryChannels/ProductDeliveryChannels';
import Quantity from '../Quantity/Quantity';
import BuyNowButton from '../BuyNowButton/BuyNowButton';
import AddToCartButton from '../AddToCartButton/AddToCartButton';
import Stack from '../Stack/Stack';
import { useControllerProps } from '../../ControllerContext';
import _ from 'lodash';
import { useState } from '../../stores';
import { SPECS } from '../../../../../consts';

const ProductPageBody = () => {
  const {
    state: { checkout },
    dispatch,
  } = useState();
  const { product, currencyCode, presetVariants, customVariant } =
    useControllerProps().giftCardStore;
  const { isEditor, isPreview, isViewer, isSSR, isMobile } = useEnvironment();
  const cascadingSettings = useCascadingSettings();
  const settings = useSettings();
  const bi = useBi();
  const { experiments } = useExperiments();

  const isDeliveryTimePickerEnabled = experiments.enabled(
    SPECS.GiftCardDeliveryTimePicker,
  );
  const showProductVariants = !!presetVariants?.length;
  const isAddToCArtButtonDisplayed = cascadingSettings.get(
    settingsParams.showAddToCartButton,
    stylesParams.shouldShowAddToCartButton,
  );
  const isBuyNowButtonDisplayed = cascadingSettings.get(
    settingsParams.showBuyNowButton,
    stylesParams.shouldShowBuyNowButton,
  );
  const isImageDisplayed = cascadingSettings.get(
    settingsParams.displayImage,
    stylesParams.shouldShowImage,
  );
  const isTitleDisplayed = cascadingSettings.get(
    settingsParams.displayTitle,
    stylesParams.shouldShowTitle,
  );
  const isDescriptionDisplayed = cascadingSettings.get(
    settingsParams.displayDescription,
    stylesParams.shouldShowDescription,
  );
  const isAmountOptionsDisplayed = cascadingSettings.get(
    settingsParams.displayAmountOptions,
    stylesParams.shouldShowAmountOptions,
  );
  const isQuantitySelectorDisplayed = cascadingSettings.get(
    settingsParams.displayQuantitySelector,
    stylesParams.shouldShowQuantitySelector,
  );
  const isC2cFormDisplayed =
    settings.get(settingsParams.giftCardFor) !== 'themselves' &&
    checkout.purchaseOptions.isGift;

  useEffect(() => {
    if (!isSSR && product) {
      bi.report({
        evid: 6,
        src: 125,
        endpoint: 'gc-client-sf',
        params: {
          productId: product.product_id,
          priceOptions: _.map(presetVariants, (variant) => variant.price).join(
            ',',
          ),
          currency: currencyCode,
          hasDeliveryDate: cascadingSettings.get(
            settingsParams.deliveryDate,
            isDeliveryTimePickerEnabled
              ? stylesParams.shouldShowDeliveryDateAndTime
              : stylesParams.shouldShowDeliveryDate,
          ),
          viewMode: isEditor
            ? 'editor'
            : isViewer
            ? 'live'
            : isPreview
            ? 'preview'
            : '',
          isAddToCartDisplayed: isAddToCArtButtonDisplayed,
          isBuyNowDisplayed: isBuyNowButtonDisplayed,
          isImageDisplayed,
          settingBuyGcFor: settings.get(settingsParams.giftCardFor),
        },
      });
    }

    setPreSelectedVariant();

    if (settings.get(settingsParams.giftCardFor) === 'themselves') {
      dispatch({
        type: 'setPurchaseOptions',
        payload: {
          isGift: false,
        },
      });
    }
    if (settings.get(settingsParams.giftCardFor) === 'both') {
      const { gift_card_for } = queryString.parse(location.search);

      switch (gift_card_for) {
        case 'myself':
          dispatch({
            type: 'setPurchaseOptions',
            payload: {
              isGift: false,
            },
          });
          break;
        case 'someoneElse':
          dispatch({
            type: 'setPurchaseOptions',
            payload: {
              isGift: true,
            },
          });
          break;
      }
    }
  }, []);

  const setPreSelectedVariant = () => {
    const { pre_selected_value, variant_id } = queryString.parse(
      location.search,
    );

    if (!showProductVariants) {
      return dispatch({
        type: 'setPurchaseOptions',
        payload: {
          variantId: 'custom',
        },
      });
    }

    const variant = presetVariants?.find(
      ({ id, value }) =>
        id.toString() === variant_id || value.toString() === pre_selected_value,
    );

    if (variant) {
      return dispatch({
        type: 'setPurchaseOptions',
        payload: {
          variantId: variant.id,
        },
      });
    }

    const firstVariant = _.first(presetVariants);
    dispatch({
      type: 'setPurchaseOptions',
      payload: {
        variantId: firstVariant?.id,
      },
    });
  };

  return (
    <>
      {product && (
        <main className={s.app}>
          <Stack
            forceColumn={isMobile}
            space={Stack.SpaceSize.large}
            responsive
          >
            {isImageDisplayed && (
              <Stack.Item cell="1">
                <ProductImage />
              </Stack.Item>
            )}
            <Stack.Item cell="1">
              {isTitleDisplayed && <Headline />}
              {showProductVariants && <ProductPrice />}
              {isDescriptionDisplayed && <ProductDescription />}
              {isAmountOptionsDisplayed && showProductVariants && (
                <ProductVariants />
              )}
              {isAmountOptionsDisplayed &&
                (checkout.purchaseOptions.variantId === 'custom' ||
                  (!showProductVariants && customVariant)) && (
                  <EnterCustomAmount />
                )}
              {isQuantitySelectorDisplayed && <Quantity />}
              {!isSSR && (
                <>
                  {settings.get(settingsParams.giftCardFor) === 'both' && (
                    <ProductBuyForSelection />
                  )}
                  {isC2cFormDisplayed && <ProductC2cForm />}
                  {!checkout.purchaseOptions.isGift && <ProductDeliveryChannels />}
                </>
              )}
              {isAddToCArtButtonDisplayed && <AddToCartButton />}
              {isBuyNowButtonDisplayed && <BuyNowButton />}
            </Stack.Item>
          </Stack>
        </main>
      )}
    </>
  );
};

export default ProductPageBody;
